import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, Building2, Building, Warehouse, TreePine, Store, MapPin, GraduationCap, Gem, TrendingUp, ArrowRight, Search, Send, Users, PhoneCall } from "lucide-react";
import api from "@/lib/api";
import SearchBar from "@/components/SearchBar";
import PropertyCard from "@/components/PropertyCard";
import PropertyCardSkeleton from "@/components/PropertyCardSkeleton";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";

const CATEGORIES = [
  { label: "Houses", icon: HomeIcon, type: "House" },
  { label: "Flats", icon: Building2, type: "Flat" },
  { label: "Apartments", icon: Building, type: "Apartment" },
  { label: "Bungalows", icon: Warehouse, type: "Bungalow" },
  { label: "New Builds", icon: TrendingUp, type: "New Build" },
  { label: "Commercial", icon: Store, type: "Commercial" },
  { label: "Land", icon: TreePine, type: "Land" },
  { label: "Student", icon: GraduationCap, type: "Student" },
  { label: "Luxury", icon: Gem, type: "Luxury" },
  { label: "Investment", icon: MapPin, type: "Investment" },
];

export default function Home() {
  const { settings } = useOutletContext();
  const [featured, setFeatured] = useState(null);
  const [latest, setLatest] = useState(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/properties/featured").then((r) => setFeatured(r.data)).catch(() => setFeatured([]));
    api.get("/properties/latest").then((r) => setLatest(r.data)).catch(() => setLatest([]));
    api.get("/blog?per_page=3").then((r) => setPosts(r.data.items || [])).catch(() => {});
  }, []);

  const hero = settings?.hero_image || "https://images.unsplash.com/photo-1721428360480-0d4121f629c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[640px] flex items-center" data-testid="hero-section">
        <img src={hero} alt="Premium UK property" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="inline-block px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-semibold tracking-wide uppercase">
              Trusted UK property partners
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
              {settings?.hero_heading || "Find Your Next Property"}
            </h1>
            <p className="mt-5 text-lg text-white/90 max-w-xl leading-relaxed">
              {settings?.hero_description || "Discover homes for sale and to let from trusted property partners across the UK. Enquire in seconds — the right agency will be in touch."}
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="mt-8 max-w-5xl">
            <SearchBar variant="hero" />
          </motion.div>
          <div className="mt-5">
            <Link to="/partner" className="text-white/90 hover:text-white text-sm font-semibold inline-flex items-center gap-1.5">
              Are you an agent? Partner with us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <SectionHeading kicker="Browse by type" title="Explore property categories" />
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.label}
              to={`/properties?property_type=${encodeURIComponent(c.type)}`}
              className="group bg-white rounded-xl border border-border p-5 flex flex-col items-center gap-3 hover:border-[#C81D35] hover:shadow-md transition-[border-color,box-shadow] duration-300"
              data-testid={`category-${c.label.toLowerCase()}`}
            >
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center group-hover:bg-[#C81D35]/10 transition-colors">
                <c.icon className="w-5 h-5 text-[#C81D35]" />
              </div>
              <span className="text-sm font-semibold text-[#111827]">{c.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="bg-[#F9FAFB] py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <SectionHeading kicker="Handpicked homes" title="Featured properties" />
            <Link to="/featured"><Button variant="outline" className="rounded-full gap-2" data-testid="view-all-featured">View all <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured === null
              ? Array.from({ length: 3 }).map((_, i) => <PropertyCardSkeleton key={i} />)
              : featured.slice(0, 6).map((p) => <PropertyCard key={p.id} property={p} />)}
            {featured?.length === 0 && <p className="text-muted-foreground col-span-full">No featured properties yet.</p>}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <SectionHeading kicker="Simple & transparent" title="How Homio works" center />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: Search, title: "Browse properties", desc: "Search homes for sale and to let from trusted property partners." },
            { icon: Send, title: "Submit an enquiry", desc: "Send a quick, no-obligation enquiry on any property you like." },
            { icon: Users, title: "We refer your enquiry", desc: "Your details are passed securely to the relevant agency or partner." },
            { icon: PhoneCall, title: "The agency contacts you", desc: "The property partner gets in touch to arrange the next steps." },
          ].map((s, i) => (
            <div key={i} className="relative bg-white rounded-2xl border border-border p-6" data-testid={`how-step-${i + 1}`}>
              <span className="absolute -top-3 left-6 w-8 h-8 rounded-full bg-[#C81D35] text-white text-sm font-bold flex items-center justify-center">{i + 1}</span>
              <s.icon className="w-8 h-8 text-[#C5A059] mt-2" />
              <h3 className="mt-4 font-semibold text-[#111827]">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Latest */}
      <section className="bg-[#F9FAFB] py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <SectionHeading kicker="Just added" title="Latest properties" />
            <Link to="/properties"><Button variant="outline" className="rounded-full gap-2">Browse all <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {latest === null
              ? Array.from({ length: 3 }).map((_, i) => <PropertyCardSkeleton key={i} />)
              : latest.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        </div>
      </section>

      {/* Why + Partner CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <SectionHeading kicker="Why Homio" title="A simpler way to find your next home" />
          <ul className="mt-6 space-y-4">
            {["Properties from multiple trusted agencies","A simple, no-obligation enquiry process","Direct communication from the relevant agency","Regularly updated listings for buyers and tenants","Support whether you're buying or renting"].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-1 w-5 h-5 rounded-full bg-[#C81D35]/10 flex items-center justify-center shrink-0">
                  <ArrowRight className="w-3 h-3 text-[#C81D35]" />
                </span>
                <span className="text-[#111827]/80">{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative rounded-3xl overflow-hidden bg-[#111827] p-10 text-white">
          <h3 className="font-display text-3xl font-bold tracking-tight">Become a property partner</h3>
          <p className="mt-3 text-white/80">Estate agents, letting agents, developers, landlords and sourcing businesses — reach motivated buyers and tenants and receive qualified enquiries.</p>
          <Link to="/partner" className="inline-block mt-6">
            <Button className="rounded-full bg-[#C81D35] hover:bg-[#a5162b] px-6" data-testid="home-partner-cta">Partner with us</Button>
          </Link>
        </div>
      </section>

      {/* Insights */}
      {posts.length > 0 && (
        <section className="bg-[#F9FAFB] py-16">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <SectionHeading kicker="Property insights" title="Guides & market news" />
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-white rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-shadow" data-testid={`insight-${p.id}`}>
                  {p.image && <div className="aspect-[16/10] overflow-hidden"><img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                  <div className="p-5">
                    <span className="text-xs font-semibold text-[#C81D35] uppercase tracking-wide">{p.category}</span>
                    <h3 className="mt-2 font-semibold text-[#111827] line-clamp-2 group-hover:text-[#C81D35] transition-colors">{p.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
                    <p className="mt-3 text-xs text-muted-foreground">{formatDate(p.published_at)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export function SectionHeading({ kicker, title, center }) {
  return (
    <div className={center ? "text-center" : ""}>
      {kicker && <span className="text-xs font-bold text-[#C81D35] uppercase tracking-[0.15em]">{kicker}</span>}
      <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#111827] tracking-tight">{title}</h2>
    </div>
  );
}
